import { useMentionCardsContext } from "@/containers/MentionCardsContext";
import { cn } from "@/lib/utils";
import { AudioMentionNew, EpisodeV4 } from "@/types/MentionCard";
import { formatMentionTiming } from "@/utils";
import { Button } from "@components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip";
import { Trans } from "@lingui/react/macro";
import { IterationCwIcon, IterationCcwIcon } from "lucide-react";
import {
  useMediaDispatch,
  useMediaSelector,
  MediaActionTypes,
} from "media-chrome/react/media-store";
import { useMemo } from "react";

export default function MentionBlip({
  episodeId,
  mention,
}: {
  episodeId: EpisodeV4["id"];
  mention: AudioMentionNew;
}) {
  const { activeEpisodeId, setActiveEpisodeId } = useMentionCardsContext();

  const dispatch = useMediaDispatch();
  const mediaCurrentTime =
    useMediaSelector((state) => state.mediaCurrentTime) ?? 0;
  const mediaError = useMediaSelector((state) => state.mediaError);
  const mediaPaused = useMediaSelector((state) => state.mediaPaused);

  const isPlayingMentionCard = useMemo(() => {
    return !mediaError && !mediaPaused && episodeId === activeEpisodeId;
  }, [mediaError, mediaPaused, episodeId, activeEpisodeId]);

  const isPlayingMention = useMemo(
    () =>
      mediaCurrentTime >= (mention.indices_metadata?.start_time ?? 0) &&
      mediaCurrentTime <= (mention.indices_metadata?.end_time ?? 0),
    [
      mediaCurrentTime,
      mention.indices_metadata?.start_time,
      mention.indices_metadata?.end_time,
    ]
  );

  const handleClick = () => {
    const detail = mention.indices_metadata?.start_time ?? 0;

    if (isPlayingMentionCard) {
      dispatch({ type: MediaActionTypes.MEDIA_SEEK_REQUEST, detail });
    } else {
      setActiveEpisodeId(episodeId);
      setTimeout(() => {
        dispatch({ type: MediaActionTypes.MEDIA_PLAY_REQUEST });
        dispatch({ type: MediaActionTypes.MEDIA_SEEK_REQUEST, detail });
      }, 0);
    }
  };

  const handleSkipBack10 = () => {
    const type = MediaActionTypes.MEDIA_SEEK_REQUEST;
    const detail = Math.max(
      mediaCurrentTime - 10,
      mention.indices_metadata?.start_time ?? 0
    );
    dispatch({ type, detail });
  };

  const handleSkipForward10 = () => {
    const type = MediaActionTypes.MEDIA_SEEK_REQUEST;
    const detail = Math.min(
      mediaCurrentTime + 10,
      mention.indices_metadata?.end_time ?? 0
    );
    dispatch({ type, detail });
  };

  return (
    <Tooltip open={isPlayingMention && isPlayingMentionCard ? true : false}>
      <TooltipTrigger
        data-testid="mention-marker"
        className={cn(
          "absolute top-0 bg-violet-400/30 h-2.5 rounded-xs transition-all"
        )}
        onClick={handleClick}
        style={{
          left: `${((mention.indices_metadata?.start_time ?? 0) / mention.total_length) * 100}%`,
          width: `${((Math.min(mention.indices_metadata?.end_time ?? 0, mention.total_length) - (mention.indices_metadata?.start_time ?? 0)) / mention.total_length) * 100}%`,
        }}
      />

      <TooltipContent side="top">
        <div className="px-1 py-0.5">
          <div className="flex items-center gap-2">
            <p className="text-xs tabular-nums">
              {formatMentionTiming(
                Math.abs(
                  mediaCurrentTime - (mention.indices_metadata?.start_time ?? 0)
                )
              )}
            </p>

            <div className="h-3 rounded-xs bg-card/20 w-48 overflow-hidden">
              <div
                className="h-full bg-violet-300 rounded-xs transition-all ease-linear"
                style={{
                  width: `${((mediaCurrentTime - (mention.indices_metadata?.start_time ?? 0)) / ((mention.indices_metadata?.end_time ?? 0) - (mention.indices_metadata?.start_time ?? 0))) * 100}%`,
                }}
              />
            </div>

            <p className="text-xs tabular-nums">
              {formatMentionTiming(
                (mention.indices_metadata?.end_time ?? 0) -
                  (mention.indices_metadata?.start_time ?? 0)
              )}
            </p>

            <div className="flex items-center -space-x-1.5">
              <Button
                size="icon-sm"
                variant="ghost"
                className="rounded-full"
                onClick={handleSkipBack10}
              >
                <IterationCwIcon />
                <span className="sr-only">
                  <Trans>Spola tillbaka 10 sekunder</Trans>
                </span>
              </Button>

              <Button
                size="icon-sm"
                variant="ghost"
                className="rounded-full"
                onClick={handleSkipForward10}
              >
                <IterationCcwIcon />
                <span className="sr-only">
                  <Trans>Spola fram 10 sekunder</Trans>
                </span>
              </Button>
            </div>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
